import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Edit, Trash2, Eye, EyeOff, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';

import { BACKEND_URL } from '../config';

const CreationsManagementPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [creations, setCreations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCreation, setEditingCreation] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    buttonText: '',
    buttonLink: '',
    published: true,
    order: 0
  });

  useEffect(() => {
    fetchCreations();
  }, []);

  const fetchCreations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/creations/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreations(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch creations',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingCreation(null);
    const maxOrder = creations.length > 0 ? Math.max(...creations.map(c => c.order)) : 0;
    setFormData({
      title: '',
      description: '',
      buttonText: '',
      buttonLink: '',
      published: true,
      order: maxOrder + 1
    });
    setShowModal(true);
  };

  const handleEdit = (creation) => {
    setEditingCreation(creation);
    setFormData({
      title: creation.title,
      description: creation.description,
      buttonText: creation.buttonText,
      buttonLink: creation.buttonLink,
      published: creation.published,
      order: creation.order
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editingCreation) {
        await axios.put(
          `${BACKEND_URL}/api/creations/${editingCreation.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast({ title: 'Success', description: 'Creation updated successfully' });
      } else {
        await axios.post(
          `${BACKEND_URL}/api/creations`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast({ title: 'Success', description: 'Creation created successfully' });
      }
      
      setShowModal(false);
      fetchCreations();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save creation',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this creation?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/creations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Creation deleted successfully' });
      fetchCreations();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete creation',
        variant: 'destructive'
      });
    }
  };

  const togglePublished = async (creation) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/creations/${creation.id}`,
        { published: !creation.published },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: 'Success', description: 'Creation updated successfully' });
      fetchCreations();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update creation',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#0d6571] shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/admin')}
                className="text-white hover:text-white/80"
              >
                <ArrowLeft className="mr-2" />
                Back to Admin
              </Button>
              <h1 className="text-2xl font-bold text-white">Creations Management</h1>
            </div>
            <Button onClick={handleCreate} className="bg-white text-[#0d6571] hover:bg-gray-100">
              <Plus className="mr-2" />
              Add Creation
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : creations.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl shadow">
            <p className="text-gray-500 mb-4">No creations yet</p>
            <Button onClick={handleCreate}>
              <Plus className="mr-2" />
              Add First Creation
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {creations.map((creation) => (
              <div key={creation.id} className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-sm text-gray-400">Order: {creation.order}</span>
                      <h3 className="text-xl font-bold text-gray-900">{creation.title}</h3>
                      {creation.published ? (
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                          Published
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">
                          Draft
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 mb-2">{creation.description}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>Button: {creation.buttonText}</span>
                      <span>→</span>
                      <a href={creation.buttonLink} target="_blank" rel="noopener noreferrer" className="text-[#0d6571] hover:underline">
                        {creation.buttonLink}
                      </a>
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => togglePublished(creation)}
                      title={creation.published ? 'Unpublish' : 'Publish'}
                    >
                      {creation.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(creation)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(creation.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold mb-6">
                {editingCreation ? 'Edit Creation' : 'Add Creation'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Title</label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0d6571]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <textarea
                    required
                    rows="3"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0d6571]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Button Text</label>
                  <input
                    type="text"
                    required
                    value={formData.buttonText}
                    onChange={(e) => setFormData({ ...formData, buttonText: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0d6571]"
                    placeholder="e.g., Visit webshop"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Button Link</label>
                  <input
                    type="url"
                    required
                    value={formData.buttonLink}
                    onChange={(e) => setFormData({ ...formData, buttonLink: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0d6571]"
                    placeholder="https://example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Display Order</label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0d6571]"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="published"
                    checked={formData.published}
                    onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                  />
                  <label htmlFor="published" className="text-sm font-medium">Publish immediately</label>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="flex-1">
                    {editingCreation ? 'Update' : 'Create'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowModal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreationsManagementPage;
